import React from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import { TimerBase } from '../TimerImages';
import {ButtonControls} from './ButtonControls';
import {StartStopButton} from './StartStopButton';
import {TimerDisplay} from './TimerDisplay';
import { EMPTY_TIME, composeTime, splitTime } from '../Timer.utils';
import className from '../style.module.scss';

const TRANSLATION_KEY = {
  HH: 'HH.string',
  MM: 'MM.string',
  SS: 'SS.string',
  Hours: 'Hours.string',
  Minutes: 'Minutes.string',
  Seconds: 'Seconds.string',
  IncreaseHourButton: 'IncreaseHourButton.string',
  DecreaseHourButton: 'DecreaseHourButton.string',
  IncreaseMinutesButton: 'IncreaseMinutesButton.string',
  DecreaseMinutesButton: 'DecreaseMinutesButton.string',
  IncreaseSecondsButton: 'IncreaseSecondsButton.string',
  DecreaseSecondsButton: 'DecreaseMinutesButton.string'
};

export function TimerControl(props) {

  const {
    t,
    //i18n
  } = useTranslation();

  const [time, setTime] = React.useState(props.time);
  React.useEffect(() => {
    if (props.time !== time) {
      setTime(props.time)
    }
  }, [props.time]);

  React.useEffect(() => {
    if (time !== props.time) {
      props.setTime(time)
    }
  }, [time]);

  const format = (value) => (value >= 10 ? `${value}` : `0${value}`);

  const startStopActive =
   props.time !== EMPTY_TIME;

  const [hour, minute, second] = splitTime(time).map(format);

  const incrementHours = () => {
    const [h, m, s] = splitTime(props.time);
    props.setTime(composeTime(h + 1, m, s));
  };

  const decrementHours = () => {
    const [h, m, s] = splitTime(props.time);
    props.setTime(composeTime(h - 1, m, s));
  };

  const incrementMinutes = () => {
    const [h, m, s] = splitTime(props.time);
    props.setTime(composeTime(h, m + 1, s));
  };

  const decrementMinutes = () => {
    const [h, m, s] = splitTime(props.time);
    props.setTime(composeTime(h, m - 1, s));
  };

  const incrementSeconds = () => {
    const [h, m, s] = splitTime(props.time);
    props.setTime(composeTime(h, m, s + 1));
  };

  const decrementSeconds = () => {
    const [h, m, s] = splitTime(props.time);
    props.setTime(composeTime(h, m, s - 1));
  };

  return (
    <div className={className.timerControl}>
      <TimerBase className={className.timerBase} />
      <div className={className.timerScreen}>
        <TimerDisplay
          timeData={{
            hours: hour,
            minutes: minute,
            seconds: second
          }}
          setTime={props.setTime}
          time={props.time}
        />
      </div>
      <div className={className.timeBtnsGroupWrapper}>
        <ButtonControls
          text={t(TRANSLATION_KEY.Hours)}
          increment={incrementHours}
          decrement={decrementHours}
          altText1={t(TRANSLATION_KEY.IncreaseHourButton, {hours: hour, minutes: minute, seconds: second})}
          altText2={t(TRANSLATION_KEY.DecreaseHourButton, {hours: hour, minutes: minute, seconds: second})}
          disabled={props.disabled}
        />
        <ButtonControls
          text={t(TRANSLATION_KEY.Minutes)}
          increment={incrementMinutes}
          decrement={decrementMinutes}
          altText1={t(TRANSLATION_KEY.IncreaseMinutesButton, {hours: hour, minutes: minute, seconds: second})}
          altText2={t(TRANSLATION_KEY.DecreaseMinutesButton, {hours: hour, minutes: minute, seconds: second})}
          disabled={props.disabled}
        />
        <ButtonControls
          text={t(TRANSLATION_KEY.Seconds)}
          increment={incrementSeconds}
          decrement={decrementSeconds}
          altText1={t(TRANSLATION_KEY.IncreaseSecondsButton, {hours: hour, minutes: minute, seconds: second})}
          altText2={t(TRANSLATION_KEY.DecreaseSecondsButton, {hours: hour, minutes: minute, seconds: second})}
          disabled={props.disabled}
        />
      </div>
      <div className={className.timerActionBtnsWrapper}>
        <StartStopButton
          onClickStart={props.startTimer}
          onClickStop={props.stopTimer}
          disabled={!startStopActive}
        />
      </div>
    </div>
  );
}

TimerControl.propTypes = {
  /**
   * Time value set on the timer
   */
  time: PropTypes.number,
  /**
   * Function to set timevalue
   */
  setTime: PropTypes.func,
  /**
   * Function to start timer
   */
  startTimer: PropTypes.func,
  /**
  * Function to stop timer
  */
  stopTimer: PropTypes.func,
  /**
   * Boolen to disable buttons
   */
  disabled: PropTypes.bool,
};
